import { useEffect, useRef } from 'react';

let gate = Promise.resolve();
let active = null;

function destroyCheckout(checkout) {
  if (!checkout) return;
  try {
    checkout.destroy();
  } catch (err) {
    // Already destroyed.
  }
  if (active === checkout) active = null;
}

function openCheckout(create) {
  let owned = null;
  let dropped = false;
  const task = gate.then(async () => {
    destroyCheckout(active);
    if (dropped) return null;
    owned = await create();
    if (dropped) {
      destroyCheckout(owned);
      owned = null;
      return null;
    }
    active = owned;
    return owned;
  });
  gate = task.then(() => {}, () => {});
  return {
    ready: task,
    drop() {
      dropped = true;
      task.then(() => destroyCheckout(owned), () => destroyCheckout(owned));
    },
  };
}

function loadStripeJs() {
  if (window.Stripe) return Promise.resolve(window.Stripe);
  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src="https://js.stripe.com/v3/"]');
    const finish = () => resolve(window.Stripe);
    if (existing) {
      if (window.Stripe) {
        finish();
        return;
      }
      existing.addEventListener('load', finish);
      existing.addEventListener('error', () => reject(new Error('Could not load the card form.')));
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.onload = finish;
    script.onerror = () => reject(new Error('Could not load the card form.'));
    document.head.appendChild(script);
  });
}

export default function StripeEmbeddedCheckout({ publishableKey, clientSecret, onComplete, onError }) {
  const mountRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  onCompleteRef.current = onComplete;
  onErrorRef.current = onError;

  useEffect(() => {
    const session = openCheckout(() => loadStripeJs().then((Stripe) => Stripe(publishableKey).initEmbeddedCheckout({
      fetchClientSecret: () => Promise.resolve(clientSecret),
      onComplete: () => {
        if (onCompleteRef.current) onCompleteRef.current();
      },
    })));
    session.ready
      .then((checkout) => {
        if (!checkout || !mountRef.current) return;
        checkout.mount(mountRef.current);
      })
      .catch((err) => {
        if (onErrorRef.current) onErrorRef.current(err);
      });
    return () => session.drop();
  }, [publishableKey, clientSecret]);

  return <div ref={mountRef} className="rd-stripe-checkout" />;
}
